import {
	ReactElement,
	useState,
	Ref,
	useRef,
	useCallback
} from "react";
import { DestinationWindow } from "client/Api/Apps/common";
import {
	Icon,
	FontIconName,
	WeekView,
	WeekViewCardStyle,
	IconValue
} from "client/Components";
import { getSubtitle, getTitle } from "./misc";
import { AppointmentKind, ZermeloAppointment } from "client/Api/Workspace/LiveInformation/Zermelo/zermeloLiveInformation";
import { UnreachableCaseError } from "client_legacy/ndaw/errors";
import { ZermeloAppointmentDetails } from "./ZermeloAppointmentDetails";
import { TimeZone } from "client/DateAndTime/timezones";

const {
	Card,
	Card: {
		Row,
		Title,
		Info
	}
} = WeekView;

export type ZermeloCardProps = {
	destinationWindow: DestinationWindow;
	isTeacher: boolean;
	appointment: ZermeloAppointment;
	timezone: TimeZone;
	onClick(): void;
};

export const ZermeloCard = (props: ZermeloCardProps): ReactElement => {
	const {
		appointment,
		isTeacher,
		destinationWindow,
		timezone,
		onClick
	} = props;

	const [isViewingDetails, setIsViewingDetails] = useState<boolean>(false);

	const ref: Ref<HTMLDivElement> = useRef<HTMLDivElement>(null);

	const configuration: CardConfiguration = getCardConfiguration(
		appointment,
		isTeacher
	);

	const clickHandler = useCallback(
		(): void => {
			onClick();
			setIsViewingDetails(true);
		},
		[onClick]
	);

	return (
		<>
			<Card
				onClick={clickHandler}
				ref={ref}
				isSelected={isViewingDetails}
				style={configuration.style}
				title={configuration.isPlaceHolder === true ? undefined : configuration.title}
				isPlaceholder={configuration.isPlaceHolder}
				icons={configuration.isPlaceHolder ? undefined : configuration.icons}
			>
				{ configuration.isPlaceHolder === true
					? (
						<Icon value={configuration.icon} />
					)
					: (
						<>
							<Row>
								<Title>
									{configuration.title}
								</Title>
							</Row>
							<Row>
								<Info>
									{configuration.subTitle}
								</Info>
							</Row>
						</>
					)
				}
			</Card>
			{isViewingDetails && ref.current && (
				<ZermeloAppointmentDetails
					target={ref.current}
					onClose={() => setIsViewingDetails(false)}
					appointment={appointment}
					destinationWindow={destinationWindow}
					variant={configuration.style}
					timezone={timezone}
				/>
			)}
		</>
	);
};
ZermeloCard.displayName = "ZermeloCard";

type CardConfiguration = {
	style: WeekViewCardStyle | undefined;
} & ({
	isPlaceHolder: false;
	icons: readonly IconValue[];
	title: string;
	subTitle: string;
} | {
	isPlaceHolder: true;
	icon: FontIconName;
});

function getCardConfiguration(
	appointment: ZermeloAppointment,
	isTeacher: boolean
): CardConfiguration {
	let style: WeekViewCardStyle | undefined;
	const icons: IconValue[] = [];

	const {
		choice,
		kind,
		isOnline,
		remarks,
		isCancelled
	} = appointment;

	if (choice.isSet) {
		if (!choice.value.hasSelected) {
			return {
				isPlaceHolder: true,
				style: choice.value.canBeChanged
					? "success"
					: "secondary",
				icon: choice.value.canBeChanged
					? "plus"
					: "lock"
			};
		}

		if (kind === AppointmentKind.Conflict) {
			icons.push({
				kind: "font",
				name: "triangle-exclamation",
				color: "danger"
			});
		} else {
			icons.push({
				kind: "font",
				name: "check",
				color: choice.value.canBeChanged
					? "success"
					: "secondary"
			});
		}
	}

	switch (kind)
	{
		case AppointmentKind.Exam:
			style = "warning";
			break;
		case AppointmentKind.Activity:
			style = "success";
			break;
		case AppointmentKind.Interlude:
			style = "secondary";
			break;
		case AppointmentKind.Choice:
		case AppointmentKind.Conflict:
		case AppointmentKind.Lesson:
		case AppointmentKind.Unknown:
			// Nothing
			break;
		default:
			throw new UnreachableCaseError(kind);
	}

	if (isOnline) {
		icons.push({
			kind: "font",
			name: "video",
			color: "secondary"
		});
	} else if (remarks.length > 0) {
		icons.push({
			kind: "font",
			name: "circle-info",
			color: "secondary"
		});
	}

	if (isCancelled) {
		icons.push({
			kind: "font",
			name: "xmark",
			color: "danger"
		});
	}

	return {
		isPlaceHolder: false,
		style,
		icons,
		title: getTitle(appointment, isTeacher),
		subTitle: getSubtitle(appointment, isTeacher, true)
	};
}

